import React, { createContext, useReducer, useContext, useMemo } from 'react';

export type Screen = 'NOTIFY_OPTIONS' | 'RESULT';

type ActionType =
  | 'SET_SCREEN'
  | 'TOGGLE_DRAWER'
  | 'RESET_STATE'
  | 'SET_SELECTED_OPTIONS'
  | 'SET_BULK_NOTIFY_SUCCESSFUL';

interface State {
  screen: Screen;
  isOpen: boolean;
  bulkNotifySuccessful: boolean;
  selectedOptions: string[];
}

export const BULK_NOTIFY_SCREENS: Record<Screen, Screen> = {
  NOTIFY_OPTIONS: 'NOTIFY_OPTIONS',
  RESULT: 'RESULT',
};

const STATE: State = {
  screen: BULK_NOTIFY_SCREENS.NOTIFY_OPTIONS,
  isOpen: false,
  bulkNotifySuccessful: null,
  selectedOptions: [],
};

type Reducer = (state: State, action: { type: ActionType; payload?: any }) => typeof STATE;

interface BulkNotifyContextType extends State {
  setScreen: (screen: Screen) => void;
  setResultScreen: () => void;
  toggleDrawer: (isOpen: boolean) => void;
  resetState: () => void;
  setBulkNotifySuccessful: (success: boolean) => void;
  setSelectedOptions: (options: string[]) => void;
}

const BulkNotifyContext = createContext<BulkNotifyContextType>(null);

export const BulkNotifyContextProvider = ({ children }) => {
  // eslint-disable-next-line no-shadow
  const [state, dispatch] = useReducer<Reducer>((state, action) => {
    switch (action.type) {
      case 'SET_SCREEN': {
        return {
          ...state,
          screen: action.payload,
        };
      }
      case 'TOGGLE_DRAWER': {
        return {
          ...state,
          isOpen: action.payload,
        };
      }

      case 'RESET_STATE': {
        return STATE;
      }
      case 'SET_BULK_NOTIFY_SUCCESSFUL': {
        return {
          ...state,
          bulkNotifySuccessful: action.payload,
        };
      }

      case 'SET_SELECTED_OPTIONS': {
        return {
          ...state,
          selectedOptions: action.payload,
        };
      }

      default: {
        return { ...state };
      }
    }
  }, STATE);

  const setScreen = (screen: Screen) => {
    dispatch({ type: 'SET_SCREEN', payload: screen });
  };

  const toggleDrawer = (isOpen: boolean) => {
    dispatch({ type: 'TOGGLE_DRAWER', payload: isOpen });
  };

  const resetState = () => {
    dispatch({ type: 'RESET_STATE' });
  };

  const setSelectedOptions = (options: string[]) => {
    dispatch({ type: 'SET_SELECTED_OPTIONS', payload: options });
  };

  const setBulkNotifySuccessful = (success: boolean) => {
    dispatch({ type: 'SET_BULK_NOTIFY_SUCCESSFUL', payload: success });
  };

  const setResultScreen = () => {
    setScreen(BULK_NOTIFY_SCREENS.RESULT);
  };

  const memoizedState = useMemo(
    () => ({
      ...state,
      setResultScreen,
      toggleDrawer,
      resetState,
      setBulkNotifySuccessful,
      setSelectedOptions,
    }),
    [state]
  );

  return <BulkNotifyContext.Provider value={memoizedState}>{children}</BulkNotifyContext.Provider>;
};

export const useBulkNotifyContext = () => useContext(BulkNotifyContext);
